import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { formatMoney, formatUK, formatWritten } from "@/lib/format";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import DatePicker from "@/components/DatePicker";
import { Users2, Plus, Pencil, Loader2, Archive, ArchiveRestore, X, FileText, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const emptySh = {
  full_name: "", is_company: false, company_name: "", reference: "", email: "",
  postal_address: "", notes: "", status: "active", shareholdings: [],
};

export default function Shareholders() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [company, setCompany] = useState(null);
  const [shareholders, setShareholders] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptySh);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailSh, setDetailSh] = useState(null);
  const [detailDivs, setDetailDivs] = useState(null);

  const openDetail = (s) => {
    setDetailSh(s);
    setDetailDivs(null);
    setDetailOpen(true);
    api.get(`/shareholders/${s.id}/dividends`).then((r) => setDetailDivs(r.data)).catch(() => setDetailDivs([]));
  };

  useEffect(() => {
    api.get("/companies").then((res) => {
      setCompanies(res.data);
      if (res.data.length) setCompanyId(res.data[0].id);
    });
  }, []);

  const load = () => {
    if (!companyId) return;
    api.get(`/companies/${companyId}`).then((r) => setCompany(r.data));
    api.get(`/companies/${companyId}/shareholders`).then((r) => setShareholders(r.data));
  };
  useEffect(() => { load(); }, [companyId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(emptySh); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ ...emptySh, ...s }); setEditId(s.id); setOpen(true); };

  const addHolding = () => set("shareholdings", [...form.shareholdings, { share_class: company?.share_classes?.[0] || "Ordinary", number_of_shares: 0, effective_date: null, ceased_date: null }]);
  const updHolding = (i, k, v) => set("shareholdings", form.shareholdings.map((h, idx) => idx === i ? { ...h, [k]: v } : h));
  const rmHolding = (i) => set("shareholdings", form.shareholdings.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.full_name) { toast.error("Full name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        shareholdings: form.shareholdings.map((h) => ({ ...h, number_of_shares: Number(h.number_of_shares) || 0 })),
      };
      if (editId) await api.put(`/shareholders/${editId}`, payload);
      else await api.post(`/companies/${companyId}/shareholders`, payload);
      toast.success("Shareholder saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleArchive = async (s) => {
    try {
      await api.post(`/shareholders/${s.id}/${s.status === "active" ? "archive" : "unarchive"}`);
      toast.success(s.status === "active" ? "Shareholder archived" : "Shareholder restored");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="shareholders-page">
      <PageHeader title="Shareholders" description="Manage shareholders and their shareholdings.">
        <div className="flex items-center gap-3">
          <Select value={companyId} onValueChange={setCompanyId}>
            <SelectTrigger className="w-64" data-testid="shareholder-company-select"><SelectValue placeholder="Select company" /></SelectTrigger>
            <SelectContent className="bg-white">
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={openNew} disabled={!companyId} data-testid="add-shareholder-button" className="bg-cass-blue text-white hover:bg-cass-bluehover">
            <Plus className="mr-2 h-4 w-4" /> Add Shareholder
          </Button>
        </div>
      </PageHeader>

      <Card className="border-cass-border shadow-sm">
        {shareholders === null ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-cass-blue" /></div>
        ) : shareholders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Users2 className="mb-3 h-10 w-10 text-cass-navy/20" />
            <p className="text-sm text-cass-navy/50">No shareholders for this company.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-cass-surface hover:bg-cass-surface">
                <TableHead className="text-xs uppercase tracking-wider">Name</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Shareholdings</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Reference</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-right text-xs uppercase tracking-wider">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shareholders.map((s) => (
                <TableRow key={s.id} className="cursor-pointer hover:bg-cass-surface" onClick={() => openDetail(s)} data-testid={`shareholder-row-${s.id}`}>
                  <TableCell className="font-medium text-cass-navy">
                    {s.full_name}
                    {s.is_company && s.company_name && <div className="text-xs text-cass-navy/50">{s.company_name}</div>}
                  </TableCell>
                  <TableCell className="text-cass-navy/70">
                    {(s.shareholdings || []).map((h, i) => <div key={i} className="tabular text-sm">{h.number_of_shares} {h.share_class}</div>)}
                  </TableCell>
                  <TableCell className="text-cass-navy/70">{s.reference || "—"}</TableCell>
                  <TableCell>
                    <Badge className={`border-0 capitalize ${s.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{s.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEdit(s); }} data-testid={`edit-shareholder-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); toggleArchive(s); }} data-testid={`archive-shareholder-${s.id}`}>
                      {s.status === "active" ? <Archive className="h-4 w-4" /> : <ArchiveRestore className="h-4 w-4" />}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-white">
          <DialogHeader><DialogTitle className="font-head text-cass-navy">{editId ? "Edit Shareholder" : "Add Shareholder"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2 flex items-center gap-3">
              <Switch checked={form.is_company} onCheckedChange={(v) => set("is_company", v)} data-testid="is-company-switch" />
              <span className="text-sm text-cass-navy/80">Shareholder is a company</span>
            </div>
            <F label="Full name *"><Input data-testid="shareholder-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} /></F>
            {form.is_company && <F label="Registered company name"><Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} /></F>}
            <F label="Reference (optional)"><Input value={form.reference} onChange={(e) => set("reference", e.target.value)} /></F>
            <F label="Email (optional)"><Input value={form.email} onChange={(e) => set("email", e.target.value)} /></F>
            <F label="Postal address" full><Textarea value={form.postal_address} onChange={(e) => set("postal_address", e.target.value)} /></F>

            <div className="col-span-2">
              <div className="mb-2 flex items-center justify-between">
                <Label className="text-sm text-cass-navy/80">Shareholdings</Label>
                <Button type="button" variant="outline" size="sm" onClick={addHolding} data-testid="add-holding"><Plus className="mr-1 h-3 w-3" /> Add</Button>
              </div>
              <div className="space-y-3">
                {form.shareholdings.map((h, i) => (
                  <div key={i} className="rounded-md border border-cass-border p-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Share class</Label>
                        <Select value={h.share_class} onValueChange={(v) => updHolding(i, "share_class", v)}>
                          <SelectTrigger data-testid={`holding-class-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white">
                            {(company?.share_classes || ["Ordinary"]).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Number of shares</Label>
                        <Input type="number" data-testid={`holding-shares-${i}`} value={h.number_of_shares} onChange={(e) => updHolding(i, "number_of_shares", e.target.value)} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Effective date</Label>
                        <DatePicker value={h.effective_date} onChange={(v) => updHolding(i, "effective_date", v)} testId={`holding-eff-${i}`} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Ceased date (optional)</Label>
                        <DatePicker value={h.ceased_date} onChange={(v) => updHolding(i, "ceased_date", v)} testId={`holding-ceased-${i}`} />
                      </div>
                    </div>
                    <Button type="button" variant="ghost" size="sm" className="mt-2 text-destructive" onClick={() => rmHolding(i)}><X className="mr-1 h-3 w-3" /> Remove</Button>
                  </div>
                ))}
              </div>
            </div>

            <F label="Notes (optional)" full><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} /></F>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-shareholder" className="bg-cass-blue text-white hover:bg-cass-bluehover">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent className="w-full overflow-y-auto bg-white sm:max-w-lg" data-testid="shareholder-detail">
          {detailSh && (
            <>
              <SheetHeader>
                <SheetTitle className="font-head text-cass-navy">{detailSh.is_company && detailSh.company_name ? detailSh.company_name : detailSh.full_name}</SheetTitle>
              </SheetHeader>
              <div className="mt-4 flex items-center justify-between">
                <Badge className={`border-0 capitalize ${detailSh.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{detailSh.status}</Badge>
                <Button variant="outline" size="sm" onClick={() => { setDetailOpen(false); openEdit(detailSh); }} data-testid="detail-edit-button">
                  <Pencil className="mr-1 h-4 w-4" /> Edit
                </Button>
              </div>

              <div className="mt-5 space-y-3 rounded-lg border border-cass-border p-4 text-sm">
                {detailSh.full_name && <Row label="Full name" value={detailSh.full_name} />}
                {detailSh.reference && <Row label="Reference" value={detailSh.reference} />}
                {detailSh.email && <Row label="Email" value={detailSh.email} />}
                {detailSh.postal_address && <Row label="Postal address" value={detailSh.postal_address} pre />}
                {detailSh.notes && <Row label="Notes" value={detailSh.notes} pre />}
              </div>

              <div className="mt-5">
                <div className="mb-2 text-xs font-bold uppercase tracking-wider text-cass-navy/50">Shareholdings</div>
                <div className="rounded-lg border border-cass-border">
                  {(detailSh.shareholdings || []).length === 0 ? (
                    <p className="p-3 text-sm text-cass-navy/50">None recorded.</p>
                  ) : (detailSh.shareholdings || []).map((h, i) => (
                    <div key={i} className="flex items-center justify-between border-b border-cass-border/60 p-3 last:border-0">
                      <span className="text-sm text-cass-navy">{h.share_class}</span>
                      <span className="tabular text-sm font-medium text-cass-navy">{h.number_of_shares} shares</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-5">
                <div className="mb-2 text-xs font-bold uppercase tracking-wider text-cass-navy/50">Dividend history (by voucher)</div>
                {detailDivs === null ? (
                  <div className="flex h-20 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-cass-blue" /></div>
                ) : detailDivs.length === 0 ? (
                  <p className="rounded-lg border border-cass-border p-3 text-sm text-cass-navy/50">No vouchers yet for this shareholder.</p>
                ) : (
                  <div className="rounded-lg border border-cass-border overflow-hidden">
                    {detailDivs.map((d) => (
                      <button key={d.id} onClick={() => { setDetailOpen(false); navigate(`/dividends/${d.id}`); }} data-testid={`detail-voucher-${d.id}`}
                        className="flex w-full items-center justify-between border-b border-cass-border/60 p-3 text-left transition-colors last:border-0 hover:bg-cass-surface">
                        <div>
                          <div className="flex items-center gap-2 text-sm font-medium text-cass-navy">
                            {d.period_label || "Dividend"}
                            <FileText className="h-3.5 w-3.5 text-cass-blue" />
                          </div>
                          <div className="text-xs text-cass-navy/50">Paid {formatUK(d.payment_date)} · {d.share_class} · <span className="capitalize">{d.status}</span></div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="tabular text-sm font-semibold text-cass-navy">{formatMoney(d.amount)}</span>
                          <ChevronRight className="h-4 w-4 text-cass-navy/40" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Row({ label, value, pre }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-cass-navy/40">{label}</div>
      <div className={`mt-0.5 text-cass-navy ${pre ? "whitespace-pre-line" : ""}`}>{value}</div>
    </div>
  );
}

function F({ label, children, full }) {
  return <div className={`space-y-1.5 ${full ? "col-span-2" : ""}`}><Label className="text-sm text-cass-navy/80">{label}</Label>{children}</div>;
}
